import { motion } from 'framer-motion';

export default function About() {
  const features = [
    { icon: 'ri-graduation-cap-line', label: 'Learn Skills', color: 'from-honey-400 to-honey-600' },
    { icon: 'ri-coin-line', label: 'Earn HMUs', color: 'from-honey-500 to-honey-700' },
    { icon: 'ri-shopping-bag-line', label: 'Shop Smart', color: 'from-honey-400 to-honey-600' },
    { icon: 'ri-gift-line', label: 'Redeem Rewards', color: 'from-honey-500 to-honey-700' },
    { icon: 'ri-trophy-line', label: 'Level Up', color: 'from-honey-400 to-honey-600' },
  ];

  return (
    <section className="py-24 bg-charcoal-800 relative overflow-hidden">
      {/* Honeycomb Pattern Background */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='52' viewBox='0 0 60 52' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l15 8.66v17.32L30 34.64 15 25.98V8.66L30 0zm0 52l15-8.66V25.98L30 17.32l-15 8.66v17.36L30 52z' fill='%23f59e0b' fill-opacity='1'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 52px'
      }}></div>

      {/* Floating Honey Drops */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          animate={{
            y: [0, -30, 0],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 4 + i,
            repeat: Infinity,
            delay: i * 0.8,
          }}
          className="absolute w-12 h-12 flex items-center justify-center"
          style={{
            left: `${15 + i * 15}%`,
            top: `${20 + (i % 3) * 20}%`,
          }}
        >
          <i className="ri-drop-fill text-4xl text-honey-400/20"></i>
        </motion.div>
      ))}

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-honey-500 text-sm font-semibold tracking-[0.2em] uppercase">
            Why HoneyJar
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-5xl lg:text-6xl font-serif text-white text-center mb-20 leading-tight max-w-5xl mx-auto"
        >
          A Revolutionary Platform Where <strong>Learning</strong> Meets <strong>Earning</strong> and <strong>Shopping</strong> Rewards You
        </motion.h2>

        {/* Feature Icons Grid - Hexagonal */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.1, rotate: 5 }}
              className="flex flex-col items-center gap-4 cursor-pointer"
            >
              <div 
                className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${feature.color} shadow-lg relative`}
                style={{
                  clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                }}
              >
                <i className={`${feature.icon} text-3xl text-white`}></i>
                
                {/* Hexagon Border Glow */}
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 border-2 border-honey-300"
                  style={{
                    clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                  }}
                />
              </div>
              <span className="text-white text-base font-medium text-center">{feature.label}</span>
            </motion.div>
          ))}
        </div>

        {/* Description Text */}
        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Honeycomb Accent */}
            <div 
              className="absolute -left-4 -top-4 w-16 h-16 bg-honey-500/10"
              style={{
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              }}
            />
            <p className="text-gray-400 text-base leading-relaxed">
              <strong className="text-white">HoneyJar</strong> revolutionizes the way you learn and earn. Complete engaging missions and educational modules to develop valuable skills while earning HMU points. Our gamified learning platform makes education rewarding, with streaks, badges, and tier-based progression that keeps you motivated every step of the way.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Honeycomb Accent */}
            <div 
              className="absolute -right-4 -top-4 w-16 h-16 bg-honey-500/10"
              style={{
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              }}
            />
            <p className="text-gray-400 text-base leading-relaxed">
              Shop smarter with our <strong className="text-white">multi-platform e-commerce integration</strong>. Connect your favorite stores like Amazon, Temu, Etsy, Shein, and TikTok Shop to earn HMUs on every view, wishlist addition, purchase, and review. Redeem your points for exclusive partner merchandise and digital products from B.E. Wear Apparel and other premium brands.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
